import { G } from '@mobily/ts-belt';
import { groupBy, keyBy } from 'lodash';
import { DataProvider } from 'react-admin';
import { WorkBook } from 'xlsx';
// eslint-disable-next-line import-x/extensions -- it tries and fails importing ESM otherwise
import { read, utils } from 'xlsx/dist/xlsx.full.min.js';

import { PAYOUT_PLAN_NAME } from '@Common/bo/b2b_campaign_plan_options';
import {
  CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE,
  CAMPAIGN_ALLOCATION_FILE_MAX_LINE,
  CAMPAIGN_ALLOCATION_FILE_VERSION,
  CAMPAIGN_COMPANY_ALLOCATOR_ALLOCATION_FILE_VERSION,
  CAMPAIGN_COMPANY_ALLOCATOR_ALLOCATION_WITH_MC_FILE_VERSION,
} from '@Common/constants/b2b/campaigns';
import { isCampaignTypeMatchCashSource } from '@Common/helpers/b2b_campaigns';
import { isFullyManagedPlan } from '@Common/types/b2b/plans';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import { B2bCampaignType, CampaignAmountType } from '@Common/types/bo/b2b/campaigns';
import { B2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts';
import {
  ZodXlsxAllocationsData,
  ZodXlsxAllocationsDataCompanyAllocatorChoice,
} from '@Common/types/bo/b2b/employees';
import { B2bCompanyPlan } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';
import { isZodError, ZodErrorLike } from '@Common/types/utils';
import { isFilePasswordProtected } from '@Common/validators/upload';

import {
  findAllocationFilePlanOption,
  keyAllocationFilePlanOptions,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/allocationFilePlans';
import { AllocationsFileError } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/allocationsFileError';
import { findDuplicateEmployeeIdentifier } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/findDuplicateEmployeeIdentifier';
import { findNegativeAllocationAmount } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/findNegativeAllocationAmount';
import { VACATION_DAYS_FILE_COLUMNS } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysColumns';
import { formatValue, parseValue } from '@sh4res/bo-common/fields';
import { convertCentsInUnits } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

export function isVersionValid(workbook: WorkBook, expected: string): boolean {
  const sheet = workbook.Sheets["Guide d'utilisation_FR"];

  if (G.isNullable(sheet)) {
    return false;
  }

  const version = sheet.E3.v;
  if (version.split('_')[1] !== expected && version.split(' ')[1] !== expected) {
    return false;
  }

  return true;
}

export { AllocationsFileError };

// Field labels matching Excel column headers
const fieldNameLabels: Record<string, string> = {
  nir: 'NIR',
  ntt: 'NTT',
  firstname: 'Prénom',
  lastname: 'Nom',
  amount: 'Montant',
  plan: 'Dispositif',
  placement_support: 'Support de placement',
  matching_contribution_amount: 'Abondement',
  ...VACATION_DAYS_FILE_COLUMNS,
};

export function convertZodErrorToAllocationsFileError(
  error: ZodErrorLike,
  dataStartingLine: number,
): AllocationsFileError {
  const [firstIssue] = error.issues;
  const { path } = firstIssue;

  const rowIndex = typeof path[0] === 'number' ? path[0] : null;
  const fieldName = typeof path[1] === 'string' ? path[1] : String(path[0]);

  const lineNumber = rowIndex !== null ? rowIndex + dataStartingLine : null;
  const fieldLabel = fieldNameLabels[fieldName] ?? fieldName;

  const hasLineNumber = lineNumber !== null;
  const messageKey = hasLineNumber
    ? 'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_field'
    : 'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_field_no_line';

  return new AllocationsFileError(
    `Validation error: ${firstIssue.message} at field ${fieldName}${hasLineNumber ? ` on line ${lineNumber}` : ''}`,
    messageKey,
    {
      field: fieldLabel,
      line: hasLineNumber ? lineNumber.toString() : '',
    },
  );
}

export function rejectDuplicateEmployeeIdentifiers(
  rows: { nir?: string | null; ntt?: string | null }[],
) {
  const duplicate = findDuplicateEmployeeIdentifier(
    rows,
    CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE,
  );
  if (G.isNullable(duplicate)) {
    return;
  }

  throw new AllocationsFileError(
    `Duplicate employee identifier ${duplicate.identifier} on lines ${duplicate.firstLine} and ${duplicate.line}`,
    'resources.bo_common.b2b.campaigns.sections.allocations.errors.duplicate_employee',
    {
      identifier: duplicate.identifier,
      firstLine: String(duplicate.firstLine),
      line: String(duplicate.line),
    },
  );
}

export function rejectNegativeAmounts(
  rows: { amount?: number | null; matching_contribution_amount?: number | null }[],
) {
  const negative = findNegativeAllocationAmount(rows, CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE);
  if (G.isNullable(negative)) {
    return;
  }

  const fieldLabel = fieldNameLabels[negative.field];
  throw new AllocationsFileError(
    `Negative amount ${negative.amount} at field ${negative.field} on line ${negative.line}`,
    'resources.bo_common.b2b.campaigns.sections.allocations.errors.negative_amount',
    {
      field: fieldLabel,
      amount: String(convertCentsInUnits(negative.amount)),
      line: String(negative.line),
    },
  );
}

export const parseFile = async ({
  file,
  amountType,
}: {
  file: File;
  amountType: CampaignAmountType;
}) => {
  const fileData = await file.arrayBuffer();
  const workbook = read(new Uint8Array(fileData), { type: 'array' });

  const [sheetName] = workbook.SheetNames;
  const sheet = workbook.Sheets[sheetName];

  if (!isVersionValid(workbook, CAMPAIGN_ALLOCATION_FILE_VERSION)) {
    throw new AllocationsFileError(
      `Invalid file version, actual is v${CAMPAIGN_ALLOCATION_FILE_VERSION}`,
      'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_file_version',
      { version: CAMPAIGN_ALLOCATION_FILE_VERSION },
    );
  }

  const rawData = utils.sheet_to_json<Record<string, string>>(sheet, {
    range: `A${CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE - 1}:AZ${CAMPAIGN_ALLOCATION_FILE_MAX_LINE}`,
  });

  try {
    const parsed = ZodXlsxAllocationsData.parse(
      rawData.map((entry) => {
        const amount =
          amountType === 'net' ? entry['Montant_total_net*'] : entry['Montant_total_brut*'];

        return {
          nir: entry['NIR*'] ?? null,
          ntt: entry.NTT,
          firstname: entry['Prénom'],
          lastname: entry.Nom,
          amount: amount ? formatValue(parseValue(Number(amount))) : null,
        };
      }),
    );
    rejectDuplicateEmployeeIdentifiers(parsed);
    rejectNegativeAmounts(parsed);
    return parsed;
  } catch (error) {
    if (isZodError(error)) {
      throw convertZodErrorToAllocationsFileError(
        error,
        CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE,
      );
    }
    throw error;
  }
};

export const parseFileCompanyAllocator = async ({
  file,
  amountType,
  campaignType,
  contractIds,
  resourceBasePath,
  dataProvider,
  matchingContributionComputedBy,
  matchingContributionRelatedCashSource,
}: {
  file: File;
  amountType: CampaignAmountType;
  campaignType: B2bCampaignType;
  contractIds: number[];
  resourceBasePath: '' | 'b2b/';
  dataProvider: DataProvider;
  matchingContributionComputedBy: B2bCampaignActionHandler | null;
  matchingContributionRelatedCashSource: B2bCashSource | null | undefined;
}) => {
  const fileData = await file.arrayBuffer();
  if (isFilePasswordProtected(new Uint8Array(fileData))) {
    throw new AllocationsFileError(
      'File provided is actually protected by a password',
      'resources.bo_common.file_upload.password_protected',
      {},
    );
  }
  const workbook = read(new Uint8Array(fileData), { type: 'array' });

  const [sheetName] = workbook.SheetNames;
  const sheet = workbook.Sheets[sheetName];

  const actualFileVersion =
    matchingContributionComputedBy === 'company'
      ? CAMPAIGN_COMPANY_ALLOCATOR_ALLOCATION_WITH_MC_FILE_VERSION
      : CAMPAIGN_COMPANY_ALLOCATOR_ALLOCATION_FILE_VERSION;

  if (!isVersionValid(workbook, actualFileVersion)) {
    throw new AllocationsFileError(
      `Invalid file version, actual is v${actualFileVersion}`,
      'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_file_version',
      { version: actualFileVersion },
    );
  }

  const { data: plans }: { data: B2bCompanyPlan[] } = await dataProvider.getList(
    `${resourceBasePath}companies/plans`,
    {
      filter: {
        b2b_contract_id: contractIds,
      },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'created_at', order: 'DESC' },
    },
  );
  const planOptions = keyAllocationFilePlanOptions({
    plans,
    campaignType,
    matchingContributionRelatedCashSource,
  });

  const instrumentIds = [
    ...new Set(
      plans.flatMap((plan) =>
        plan.shares_plans.flatMap((sharesPlan) => sharesPlan.instruments_universe),
      ),
    ),
  ];
  let instruments: GetInstrumentsResponseItem[] = [];
  if (instrumentIds.length > 0) {
    const { data } = await dataProvider.getList('instruments', {
      filter: {
        id: instrumentIds,
      },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: isBoExternal() ? 'id' : 'created_at', order: 'DESC' },
    });
    instruments = data;
  }
  const instrumentsMap = keyBy(instruments, (instrument) =>
    (instrument.isin ?? '').trim().toLowerCase(),
  );

  const rawData = utils.sheet_to_json<Record<string, string>>(sheet, {
    range: `A${CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE - 1}:AZ${CAMPAIGN_ALLOCATION_FILE_MAX_LINE}`, // We may have a problem here later : https://sh4res.atlassian.net/browse/BRAV-10270
  });

  assertAmountsExistsInEntryFile({
    data: rawData,
    amountType,
    campaignType,
    matchingContributionComputedBy,
  });

  let choices;
  try {
    choices = ZodXlsxAllocationsDataCompanyAllocatorChoice.parse(
      rawData.map((entry) => {
        const { amountColumn, amountMcColumn } = getAmountsColumns({ amountType, campaignType });
        const amount = entry[amountColumn];
        const amountMc = entry[amountMcColumn];

        return {
          nir: entry['NIR*'] ?? null,
          ntt: entry.NTT,
          firstname: entry['Prénom'] ?? entry.Prenom,
          lastname: entry.Nom,
          amount: G.isNotNullable(amount) ? formatValue(parseValue(Number(amount))) : null,
          plan: entry['Dispositif*'],
          placement_support: entry['Support de placement'],
          matching_contribution_amount:
            matchingContributionComputedBy && amountMc
              ? formatValue(parseValue(Number(amountMc)))
              : null,
        };
      }),
    );
  } catch (error) {
    if (isZodError(error)) {
      throw convertZodErrorToAllocationsFileError(
        error,
        CAMPAIGN_ALLOCATION_FILE_DATA_STARTING_LINE,
      );
    }
    throw error;
  }

  rejectNegativeAmounts(choices);

  const choicesGrouped = groupBy(choices, (choice) => choice.nir ?? choice.ntt);

  return Object.entries(choicesGrouped).map(([_nir, choices]) => {
    let amount = 0;
    let mcAmount = 0;
    const choicesParsed = choices
      .filter((choice) => G.isNotNullable(choice.plan))

      .map((choice) => {
        amount += choice.amount ?? 0;
        mcAmount += choice.matching_contribution_amount ?? 0;

        const planName = choice.plan!; // eslint-disable-line @typescript-eslint/no-non-null-assertion -- null filtered out

        if (planName === PAYOUT_PLAN_NAME) {
          return {
            allocation_type: 'payout',
            amount: choice.amount,
            plan_id: 'payout',
          };
        }

        const instrument =
          instrumentsMap[(choice.placement_support?.split(' - ')[0] ?? '').toLowerCase()];

        const option = findAllocationFilePlanOption({ planOptions, planName, instrument });
        const { plan, deductibility } = option;

        if (isFullyManagedPlan(plan.type)) {
          // TODO: risk_profile removed from MVP, to be brought back later https://sh4res.atlassian.net/browse/BRAV-8365
          return {
            allocation_type: 'managed_plan',
            amount: choice.amount,
            plan_id: plan.id,
            risk_profile: undefined,
            plan_type: plan.type,
            deductibility: deductibility ?? undefined,
            plan_label: plan.plan_label,
            matching_contribution_amount: choice.matching_contribution_amount,
          };
        }

        if (
          choice.placement_support &&
          // Instrument not found
          (G.isNullable(instrument) ||
            // Instrument not part of the plan instrument universe
            !plan.instruments_universe.includes(instrument.id) ||
            // CCB instrument selected on campaign different than participation
            (campaignType !== 'participation' && instrument.type === 'ccb') ||
            // Instrument not enabled for this specific cash source
            (isCampaignTypeMatchCashSource(campaignType) &&
              plan.is_cash_sources_per_instrument &&
              !plan.instruments_cash_sources_libre
                .find((entry) => entry.instrument_id === instrument.id)
                ?.cash_sources?.includes(campaignType)))
        ) {
          throw new AllocationsFileError(
            `${choice.placement_support} is not enabled in ${plan.name} for ${campaignType}`,
            'resources.bo_common.b2b.campaigns.sections.allocations.errors.invalid_instrument',
            { instrument: choice.placement_support, plan: plan.name, campaign: campaignType },
          );
        }

        return {
          allocation_type: 'plan',
          amount: choice.amount,
          plan_id: plan.id,
          instrument_id: instrument.id,
          deductibility: deductibility ?? undefined,
          plan_type: plan.type,
          plan_label: plan.plan_label,
          matching_contribution_amount: choice.matching_contribution_amount,
        };
      });

    return {
      nir: choices[0].nir,
      ntt: choices[0].ntt,
      firstname: choices[0].firstname,
      lastname: choices[0].lastname,
      amount,
      company_collected_choices: choicesParsed,
      matching_contribution_amount: mcAmount,
    };
  });
};

const assertAmountsExistsInEntryFile = ({
  data,
  amountType,
  campaignType,
  matchingContributionComputedBy,
}: {
  data: Record<string, string>[];
  amountType: CampaignAmountType;
  campaignType: B2bCampaignType;
  matchingContributionComputedBy: B2bCampaignActionHandler | null;
}) => {
  const { amountColumn, amountMcColumn } = getAmountsColumns({ amountType, campaignType });

  if (
    campaignType !== 'matching_contribution' &&
    !data.some((entry) => !G.isUndefined(entry[amountColumn]))
  ) {
    throw new AllocationsFileError(
      'Amount column not found in the file, please verify the title of the column.',
      'resources.bo_common.b2b.campaigns.sections.allocations.errors.amount_column_not_found',
    );
  }

  if (
    matchingContributionComputedBy === 'company' &&
    !data.some((entry) => !G.isUndefined(entry[amountMcColumn]))
  ) {
    throw new AllocationsFileError(
      'Matching contribution column not found in the file, please verify the title of the column.',
      'resources.bo_common.b2b.campaigns.sections.allocations.errors.mc_column_not_found',
    );
  }
};

const getAmountsColumns = ({
  amountType,
  campaignType,
}: {
  amountType: CampaignAmountType;
  campaignType: B2bCampaignType;
}) => {
  const amountColumn =
    amountType === 'net'
      ? `Montant net${campaignType === 'voluntary_deposit' ? ' du versement' : ''}*`
      : 'Montant brut*';

  const amountMcColumn =
    amountType === 'net' && campaignType !== 'voluntary_deposit'
      ? `Abondement net${campaignType === 'matching_contribution' ? '*' : ''}`
      : `Abondement brut${campaignType === 'matching_contribution' ? '*' : ''}`;

  return { amountColumn, amountMcColumn };
};
